import { useEffect } from "react";
import { useNavigate, useRouterState } from "@tanstack/react-router";

import { isEntityLevelRoute, isMergedViewHiddenRoute } from "@/constants/routesConfig";
import { useActiveSession } from "@/hooks/useActiveSession";
import { useEntityTenantStore } from "@/store/entityTenantStore";
import { useImpersonationStore } from "@/store/useImpersonationStore";
import {
  isCrossTenantForSuperAdmin,
  isInsideEntity,
  isMergedView,
  isPlatformView,
  isSuperAdminPlatformView,
} from "@/utils/isPlatformView";

/**
 * Reactive form of `isPlatformView`. Reads through `useActiveSession` so an
 * impersonated session is judged by the impersonated user's level, not the
 * actor's.
 */
export function usePlatformView(): boolean {
  const { isEntityLogin } = useActiveSession();
  return isPlatformView({ isEntityLogin });
}

/** Reactive form of `isCrossTenantForSuperAdmin`; see that function for why it differs. */
export function useCrossTenantForSuperAdmin(): boolean {
  const { userType } = useActiveSession();
  return isCrossTenantForSuperAdmin({ userType });
}

/** Reactive form of `isSuperAdminPlatformView`; see that function for why Proposals differs. */
export function useSuperAdminPlatformView(): boolean {
  const { userType, isEntityLogin } = useActiveSession();
  return isSuperAdminPlatformView({ userType, isEntityLogin });
}

/** Reactive form of `isInsideEntity`; see that function for the two routes it covers. */
export function useInsideEntity(): boolean {
  const { isEntityLogin } = useActiveSession();
  const entityTenant = useEntityTenantStore((state) => state.entityTenant);
  const isImpersonating = useImpersonationStore((state) => state.isImpersonating);
  return isInsideEntity({ isEntityLogin, entityTenant, isImpersonating });
}

/**
 * For a screen that exists only at ServEngine level. The route guard closes its
 * URL, but the guard runs on navigation only: a super admin already on the screen
 * who picks an entity in the label menu would otherwise keep it, now showing
 * that entity's rows under its name.
 */
export function useLeaveWhenInsideEntity(): void {
  const navigate = useNavigate();
  const insideEntity = useInsideEntity();

  useEffect(() => {
    if (insideEntity) void navigate({ to: "/", replace: true });
  }, [insideEntity, navigate]);
}

/** Reactive form of `isMergedView`; see that function for who it covers. */
export function useMergedView(): boolean {
  const { isAppLevel, allEntities, userType, isEntityLogin } = useActiveSession();
  const entityTenant = useEntityTenantStore((state) => state.entityTenant);
  const isImpersonating = useImpersonationStore((state) => state.isImpersonating);
  return isMergedView({
    isAppLevel: isAppLevel ?? false,
    allEntities: allEntities ?? false,
    userType,
    isEntityLogin,
    entityTenant,
    isImpersonating,
  });
}

/**
 * The merged-view twin of `useLeaveWhenInsideEntity`: an app-level user on a
 * screen her entity allows who switches the label menu back to ServEngine
 * would otherwise keep a screen that has no merged read.
 */
export function useLeaveWhenMergedView(): void {
  const navigate = useNavigate();
  const mergedView = useMergedView();
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  useEffect(() => {
    if (mergedView && (isMergedViewHiddenRoute(pathname) || isEntityLevelRoute(pathname))) {
      void navigate({ to: "/", replace: true });
    }
  }, [mergedView, pathname, navigate]);
}
